import { useState } from 'preact/hooks';
import { PostCard } from './post-card';
import { PostComposer } from './post-composer';
import { PinnedPostsGrid } from './pinned-posts-grid';
import { rpx } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import type { CommunityFeedResponse, CommunityCategories } from './types';

type Props = {
  initialFeed: CommunityFeedResponse;
  pinnedDiscussions: CommunityFeedResponse['discussions'];
  categories: CommunityCategories;
  canPin: boolean;
  canSchedule: boolean;
  canHide: boolean;
};

export function DiscussionFeed({
  initialFeed,
  pinnedDiscussions: initialPinned,
  categories,
  canPin,
  canSchedule,
  canHide,
}: Props) {
  const [feed, setFeed] = useState(initialFeed);
  const [pinned, setPinned] = useState(initialPinned);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  async function refreshFeed() {
    try {
      const [newFeed, { discussions: newPinned }] = await Promise.all([
        rpx.miraseeCommunity.getCommunityFeed({}),
        rpx.miraseeCommunity.getCommunityFeed({ pinnedOnly: true }),
      ]);
      setFeed(newFeed);
      setPinned(newPinned);
    } catch (err) {
      showError(err);
    }
  }

  async function loadMore() {
    if (!feed.hasMore || isLoadingMore) {
      return;
    }
    setIsLoadingMore(true);
    try {
      const moreFeed = await rpx.miraseeCommunity.getCommunityFeed({
        cursor: feed.cursor,
      });
      setFeed((prev) => ({
        discussions: [...prev.discussions, ...moreFeed.discussions],
        hasMore: moreFeed.hasMore,
        cursor: moreFeed.cursor,
      }));
    } catch (err) {
      showError(err);
    } finally {
      setIsLoadingMore(false);
    }
  }

  return (
    <div class="flex-1 min-w-0">
      <PostComposer onPostCreated={refreshFeed} categories={categories} canSchedule={canSchedule} />

      <PinnedPostsGrid
        pinned={pinned}
        canPin={canPin}
        canHide={canHide}
        onPinToggled={refreshFeed}
        onHideToggled={refreshFeed}
        onCommentAdded={refreshFeed}
        onPostEdited={refreshFeed}
      />

      {feed.discussions.map((discussion) => (
        <PostCard
          key={discussion.id}
          discussion={discussion}
          onCommentAdded={refreshFeed}
          onPostEdited={refreshFeed}
          canPin={canPin}
          onPinToggled={refreshFeed}
          canHide={canHide}
          onHideToggled={refreshFeed}
        />
      ))}

      {pinned.length === 0 && feed.discussions.length === 0 && (
        <div class="text-center py-12 text-gray-400">
          <p class="text-lg font-medium">No posts yet</p>
          <p class="text-sm mt-1">Be the first to share something with the community!</p>
        </div>
      )}

      {feed.hasMore && (
        <div class="flex justify-center py-4">
          <button
            class="inline-flex items-center justify-center rounded-full border border-gray-300 dark:border-gray-600 px-6 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition disabled:opacity-50"
            onClick={loadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
}
